import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { InlineAlert } from '../../components/InlineAlert'
import { MandatoryCommentModal } from '../../components/MandatoryCommentModal'
import { Popover } from '../../design-system/list-view/Popover'
import { ApiError } from '../../api/problem'
import { adjustSpendLine, listSpendLineAdjustments } from '../../api/film'
import { label } from '../../lib/labels'
import type { QualifiedSpendLineView, SpendAdjustmentView } from '../../types/film'

export interface SpendGridProps {
  productionId: string
  rows: QualifiedSpendLineView[]
  /** Called with the server's fresh row after a successful adjustment — never applied optimistically. */
  onRowUpdated: (row: QualifiedSpendLineView) => void
}

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Manual',
  computed: 'Computed',
  redistributed: 'Redistributed',
  imported: 'Imported',
}

const SOURCE_VARIANT: Record<string, StatusPillVariant> = {
  manual: 'info',
  computed: 'neutral',
  redistributed: 'warning',
  imported: 'muted',
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

const METHOD_LABEL: Record<string, string> = {
  manual: 'Manual',
  computed: 'Computed',
  redistributed: 'Redistributed',
  ai_accept: 'AI accept',
  unlock: 'Unlock',
}

const METHOD_VARIANT: Record<string, StatusPillVariant> = {
  manual: 'info',
  computed: 'neutral',
  redistributed: 'warning',
  ai_accept: 'success',
  unlock: 'muted',
}

/**
 * The real adjustment-history flyout body (IB-155/IB-53b, replaces the Slice-A "coming soon"
 * stub) — fetches `GET /qualified-spend-lines/{id}/adjustments` on mount (i.e. each time the
 * Popover opens, since the parent only mounts this when `historyOpenId === row.id`). Renders one
 * of four honest states: loading, a retry-capable error (never silently falling back to a "coming
 * soon" message — AC7), an explicit empty state (AC6), or the newest-first list of real ledger
 * rows (method/before→after/comment/actor/channel/timestamp).
 */
function AdjustmentHistoryContent({ spendLineId }: { spendLineId: string }) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [records, setRecords] = useState<SpendAdjustmentView[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listSpendLineAdjustments(spendLineId)
      .then((data) => {
        setRecords(data)
        setState('ready')
      })
      .catch((err) => {
        setError(
          err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not load adjustment history. Please try again.',
        )
        setState('error')
      })
  }, [spendLineId])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return (
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }} role="status">
        Loading adjustment history…
      </p>
    )
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Retry
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  if (records.length === 0) {
    return (
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>No adjustments recorded yet.</p>
    )
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      {records.map((record, index) => (
        <li key={`${record.appliedAt}-${index}`} style={{ borderBottom: '1px solid var(--iw-border)', paddingBottom: 'var(--iw-space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
            <StatusPill
              variant={METHOD_VARIANT[record.method] ?? 'neutral'}
              label={METHOD_LABEL[record.method] ?? record.method}
            />
            <strong>
              {record.priorValue !== null ? `${record.priorValue}%` : '—'} → {record.newValue}%
            </strong>
          </div>
          <p style={{ margin: '4px 0 0' }}>{record.reason}</p>
          <p style={{ margin: '4px 0 0', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            {record.channel} · {new Date(record.appliedAt).toLocaleString()}
          </p>
        </li>
      ))}
    </ul>
  )
}

/**
 * The film twin of QREGrid (design-system.md §3.3) — Slice A scope: read
 * every field the API returns (AC7), the ONE guarded adjust path (AC8/AC9),
 * locked-row disabled-with-tooltip (AC10), and (IB-155/IB-53b) the real
 * adjustment-history flyout over `GET /qualified-spend-lines/{id}/adjustments`
 * — replacing Slice A's "coming soon" affordance (C-2, AC1/AC6/AC7).
 *
 * Deferred, named (not silently dropped): the full custom spreadsheet
 * keyboard model (arrows/Home/End/PageUp-Dn, design-system §3.3) — this
 * slice uses standard Tab/Enter semantics on a real `<table>`, which is fully
 * operable but not the bespoke roving-tabindex grid `DataGrid` implements
 * for `ListView`. Water-fill redistribute (AC12) is a fast-follow within
 * Slice A per the design's own scope note (§3.4) — not built here.
 */
export function SpendGrid({ rows, onRowUpdated }: SpendGridProps) {
  const [editing, setEditing] = useState<QualifiedSpendLineView | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null)

  const sorted = useMemo(() => [...rows].sort((a, b) => a.category.localeCompare(b.category)), [rows])

  async function handleSubmitAdjustment(afterPct: number, comment: string) {
    if (!editing) return
    setSubmitting(true)
    setServerError(null)
    try {
      const updated = await adjustSpendLine(editing.id, { afterPct, comment })
      onRowUpdated(updated)
      setEditing(null)
    } catch (err) {
      setServerError(
        err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not save this adjustment. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <table className="iw-table" aria-label="Qualified spend lines">
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Amount</th>
            <th scope="col">In-state</th>
            <th scope="col">Qualifying %</th>
            <th scope="col">Source</th>
            <th scope="col">Lock</th>
            <th scope="col">History</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id}>
              <td>{label('spendCategory', row.category)}</td>
              <td>{formatAmount(row.amount, row.currency)}</td>
              <td>
                {/* inState is @GuardedField with no write channel in this slice — always read-only (AC13-analog for spend lines). */}
                {row.inState === null ? (
                  <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>
                ) : (
                  <StatusPill
                    variant={row.inState ? 'success' : 'neutral'}
                    label={row.inState ? 'In-state' : 'Out-of-state'}
                  />
                )}
              </td>
              <td>
                {row.locked ? (
                  <button
                    type="button"
                    className="iw-btn iw-btn--sm"
                    disabled
                    title="Unlock first"
                    aria-label={`Qualifying percent locked for ${label('spendCategory', row.category)} — unlock first`}
                  >
                    <Icon name="lock" size={12} />
                    {row.qualifyingPct !== null ? `${row.qualifyingPct}%` : 'Not yet assessed'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="iw-btn iw-btn--sm"
                    onClick={() => {
                      setServerError(null)
                      setEditing(row)
                    }}
                    aria-label={`Adjust qualifying percent for ${label('spendCategory', row.category)}`}
                  >
                    <Icon name="edit-3" size={12} />
                    {row.qualifyingPct !== null ? `${row.qualifyingPct}%` : 'Not yet assessed'}
                  </button>
                )}
              </td>
              <td>
                {row.qualifyingPctSource ? (
                  <StatusPill
                    variant={SOURCE_VARIANT[row.qualifyingPctSource] ?? 'neutral'}
                    label={SOURCE_LABEL[row.qualifyingPctSource] ?? row.qualifyingPctSource}
                  />
                ) : (
                  <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>
                )}
              </td>
              <td>
                <Icon name={row.locked ? 'lock' : 'unlock'} size={14} />
                <span style={{ marginLeft: 4 }}>{row.locked ? 'Locked' : 'Unlocked'}</span>
              </td>
              <td>
                <Popover
                  label={`Adjustment history for ${label('spendCategory', row.category)}`}
                  triggerContent={<Icon name="clock" size={14} />}
                  open={historyOpenId === row.id}
                  onOpenChange={(open) => setHistoryOpenId(open ? row.id : null)}
                  triggerClassName="iw-btn iw-btn--sm"
                >
                  <div style={{ padding: 'var(--iw-space-3)', fontSize: 'var(--iw-size-sm)', minWidth: 260 }}>
                    <p style={{ margin: '0 0 var(--iw-space-2)', fontWeight: 'var(--iw-weight-medium)' as never }}>
                      Adjustment history
                    </p>
                    {historyOpenId === row.id ? <AdjustmentHistoryContent spendLineId={row.id} /> : null}
                  </div>
                </Popover>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sorted.length === 0 ? (
        <InlineAlert severity="info">No qualified spend lines yet for this production.</InlineAlert>
      ) : null}

      {editing ? (
        <MandatoryCommentModal
          title={`Adjust qualifying % — ${label('spendCategory', editing.category)}`}
          fieldLabel="Qualifying %"
          beforeValue={editing.qualifyingPct}
          min={0}
          max={100}
          submitting={submitting}
          serverError={serverError}
          onClose={() => {
            if (submitting) return
            setEditing(null)
            setServerError(null)
          }}
          onSubmit={(afterValue, comment) => void handleSubmitAdjustment(afterValue, comment)}
        />
      ) : null}
    </div>
  )
}
